import Image from "next/image";
import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { setCityAndZipcodeCookies } from "@/utils/locationDetails";

import { ChooseShippingAddressModal } from "@/app/ec/product/[basePath]/shipping-address/chooseShippingAddressModal";
import { actions } from "@/store/searchSlice";
import Cookies from "js-cookie";
import styles from "./deliveryToAndLocation.module.scss";

const DeliveryToAndLocation: FunctionComponent<DeliveryToAndLocationProps> = ({
  id,
  variant = "filterPanel",
  hideBelowLg = false,
  deliveryToUser,
  updateLocationDetails,
}) => {
  const [showChooseShippingDeliveryAddressModal, setShowChooseShippingDeliveryAddressModal] = useState(false);
  const dispatch = useDispatch();

  const [hydrated, setHydrated] = useState(false); // Tracks if client-side hydration is complete
  const [city, setCity] = useState<string | undefined>(deliveryToUser?.city?.toLowerCase());
  const [zipCode, setZipCode] = useState<string | undefined>(deliveryToUser?.zipCode);

  const defaultProps = {
    id: id,
    className: [styles[VARIANT_CLASS[variant]], hideBelowLg ? styles["delivery-details-in-filter-by-panel-mobile"] : ""]
      .filter(Boolean)
      .join(" "),
  };

  const closeChooseShippingDeliveryAddressModal = useCallback(() => {
    setShowChooseShippingDeliveryAddressModal(false);
    if (updateLocationDetails) {
      updateLocationDetails();
    }
  }, [updateLocationDetails]);

  useEffect(() => {
    // Set hydrated to true after the component mounts on the client
    setHydrated(true);

    // Only update city and zipCode cookies on the client
    if (deliveryToUser?.zipCode && deliveryToUser?.city) {
      const currentCity = deliveryToUser.city.toLowerCase();
      setCityAndZipcodeCookies(currentCity, deliveryToUser.zipCode);

      const cookieZip = Cookies.get("n32UserPrimaryZip") || deliveryToUser.zipCode;
      setZipCode(cookieZip);
      setCity(currentCity);
      dispatch(actions.setShippingZipCode(cookieZip));
    }
  }, [deliveryToUser, dispatch]);

  if (!hydrated) {
    // Avoid mismatched rendering by showing a placeholder or empty content during hydration
    return null;
  }

  return (
    <div {...defaultProps}>
      <div className="d-flex" onClick={() => setShowChooseShippingDeliveryAddressModal(true)}>
        <Image
          width={12}
          height={16}
          alt="Map pin icon"
          src="/next-public/common/icons/location-pin-icon.svg"
          className={styles.mapPin}
          unoptimized
        />
        <span className={styles["details-section"]}>
          <span className={styles["deliver-to-text"]}>Deliver to </span>
          <span className={styles["city-and-zipcode"]}>
            {city}, {zipCode}
          </span>
        </span>
      </div>
      {showChooseShippingDeliveryAddressModal && (
        <ChooseShippingAddressModal
          showModal={showChooseShippingDeliveryAddressModal}
          showManualZipCode={true}
          chooseShippingAddressModalCallBack={closeChooseShippingDeliveryAddressModal}
        />
      )}
    </div>
  );
};

type DeliveryToAndLocationVariant = "productDetail" | "filterPanel";

type DeliveryToAndLocationProps = {
  id?: string;
  variant?: DeliveryToAndLocationVariant;
  hideBelowLg?: boolean;
  deliveryToUser?: DeliveryToUser;
  updateLocationDetails?: (() => void) | undefined;
};

const VARIANT_CLASS: Record<DeliveryToAndLocationVariant, string> = {
  productDetail: "delivery-to-and-location",
  filterPanel: "delivery-details-in-filter-by-panel",
};

export default DeliveryToAndLocation;
